export type FrameCallback = (dt: number, now: number) => void;

export type Engine = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  dpr: number;
  start: (onFrame: FrameCallback) => void;
  stop: () => void;
  dispose: () => void;
};

const DPR_CAP = 2;

export function createEngine(canvas: HTMLCanvasElement): Engine {
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
  if (!ctx) throw new Error("2D canvas context unavailable");

  const engine: Engine = {
    canvas,
    ctx,
    width: 0,
    height: 0,
    dpr: 1,
    start: () => undefined,
    stop: () => undefined,
    dispose: () => undefined,
  };

  let raf = 0;
  let running = false;
  let last = 0;
  let frameCb: FrameCallback | null = null;

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    const w = window.innerWidth;
    const h = window.innerHeight;
    engine.dpr = dpr;
    engine.width = w;
    engine.height = h;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  resize();
  window.addEventListener("resize", resize);

  const tick = (now: number) => {
    if (!running) return;
    if (!last) last = now;
    // Clamp dt to avoid huge jumps after tab sleep.
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    frameCb?.(dt, now);
    raf = requestAnimationFrame(tick);
  };

  engine.start = (onFrame: FrameCallback) => {
    frameCb = onFrame;
    if (running) return;
    running = true;
    last = 0;
    raf = requestAnimationFrame(tick);
  };

  engine.stop = () => {
    running = false;
    cancelAnimationFrame(raf);
  };

  engine.dispose = () => {
    engine.stop();
    window.removeEventListener("resize", resize);
  };

  return engine;
}

export function clearBlack(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);
}

/** Soft vignette: edges slightly darker so the center reads better. */
export function drawVignette(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
) {
  const g = ctx.createRadialGradient(
    w * 0.5,
    h * 0.5,
    Math.min(w, h) * 0.2,
    w * 0.5,
    h * 0.5,
    Math.max(w, h) * 0.72,
  );
  g.addColorStop(0, "rgba(0,0,0,0)");
  g.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}
